import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { CommonModule } from '@angular/common'
import { Component, inject, Signal, signal } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatTableModule } from '@angular/material/table'
import { MatButtonModule } from '@angular/material/button'
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { ConciergeAddBenefitService } from './add-benefit.service'
import { HttpErrorResponse } from '@angular/common/http'
import { GenericErrorComponent } from '../../../../../simple-bottom-sheets/generic-error.component'
import { ConciergeModalSucessComponent } from '../../../modal-sucess/modal-success.component'
import { CalendarUiComponent } from '@monorepo-channels/components/ui-pm'
import { MatOptionModule } from '@angular/material/core'
import { MatSelectModule } from '@angular/material/select'
import { Client } from '../../../control-panel.service'

interface FieldState {
	focused: Signal<boolean>
	editing: Signal<boolean>
	setFocused: (value: boolean) => void
	setEditing: (value: boolean) => void
}

@Component({
	standalone: true,
	selector: 'feature-pm-concierge-add-benefit-content',
	templateUrl: './add-benefit.component.html',
	styleUrls: ['./add-benefit.component.scss'],
	imports: [
		MatTableModule,
		CommonModule,
		MatIconModule,
		MatButtonModule,
		MatBottomSheetModule,
		ReactiveFormsModule,
		FormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCheckboxModule,
		CalendarUiComponent,
		MatOptionModule,
		MatSelectModule,
	],
})
export class ConciergeAddBenefitComponent {
	private fb = inject(FormBuilder)
	private bottomSheet = inject(MatBottomSheet)
	private dialogRef = inject(MatDialogRef<ConciergeAddBenefitComponent>)
	private addBenefitService = inject(ConciergeAddBenefitService)
	public client = inject<Client>(MAT_DIALOG_DATA)
	private dialog = inject(MatDialog)
	public availabilityDates: Date[] = []
	calendarField: 'validFrom' | 'validUntil' | null = null

	private fieldStates = new Map<string, FieldState>()

	public benefitForm = this.fb.nonNullable.group({
		description: [{ value: '', disabled: false }, [Validators.required]],
		value: [{ value: 300, disabled: false }, [Validators.required]],
		validFrom: [{ value: '', disabled: false }, [Validators.required]],
		validUntil: [{ value: '', disabled: false }, [Validators.required]],
	})

	constructor() {
		this.generateAvailabilityDates()
		;['description', 'value', 'validFrom', 'validUntil'].forEach(fieldName => {
			this.initializeFieldState(fieldName)
		})
	}

	hasCenturionCard(): boolean {
		return this.client.categories.some(c => c.categoryId === '64d3f4f23ef0bd49cf0f3a82')
	}

	toggleCalendar(field: 'validFrom' | 'validUntil') {
		this.calendarField = this.calendarField === field ? null : field
	}

	setDate(field: 'validFrom' | 'validUntil', date: Date) {
		const formattedDate = date.toISOString().substring(0, 10)
		this.benefitForm.get(field)?.setValue(formattedDate)
		this.calendarField = null
	}

	private generateAvailabilityDates() {
		const today = new Date()
		const threeMonthsLater = new Date()
		threeMonthsLater.setMonth(today.getMonth() + 3)

		const dates: Date[] = []

		for (let date = new Date(today); date <= threeMonthsLater; date.setDate(date.getDate() + 1)) {
			dates.push(new Date(date))
		}

		this.availabilityDates = dates
	}

	private initializeFieldState(fieldName: string) {
		const focused = signal(false)
		const editing = signal(false)

		this.fieldStates.set(fieldName, {
			focused,
			editing,
			setFocused: (value: boolean) => focused.set(value),
			setEditing: (value: boolean) => editing.set(value),
		})
	}

	getFieldState(fieldName: string): FieldState {
		return this.fieldStates.get(fieldName)!
	}

	closeModal() {
		this.dialogRef.close()
	}

	save() {
		if (this.benefitForm.invalid) return

		const formData = this.benefitForm.getRawValue()
		const conciergeUser = localStorage.getItem('CONCIERGE_USER') ?? undefined

		let conciergeUserId: string | undefined
		try {
			const conciergeUserParsed = conciergeUser ? JSON.parse(conciergeUser) : null
			conciergeUserId = conciergeUserParsed?._id
		} catch (e) {
			console.error('Erro ao fazer parse do concierge user do localStorage', e)
		}

		const voucherToCreate = {
			...formData,
			clientId: this.client._id,
			enabled: true,
			conciergeUser: conciergeUserId,
			status: 'active',
			reason: '',
		}
		this.addBenefitService.create(voucherToCreate).subscribe({
			next: () => {
				this.dialog.open(ConciergeModalSucessComponent, { data: 'Benefício concedido!' })
				this.dialogRef.close('updated')
			},
			error: (err: HttpErrorResponse) => {
				const errorMessage = err.error?.message || 'Erro desconhecido'
				this.bottomSheet.open(GenericErrorComponent, {
					data: {
						error: errorMessage,
						title: 'Erro ao adicionar benefício',
						btnMessage: 'Fechar',
					},
				})
			},
		})
	}
}
